// ============================================================
// MySQLAssignmentSolution.js
//
// COMPLETE SOLUTION — MySQL Capstone Assignment
// Covers Full MySQL Curriculum + DBA Responsibilities
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Divider,
    Paper,
    Chip,
} from "@mui/material";

/* Icons */
import SchoolIcon from "@mui/icons-material/School";
import StarIcon from "@mui/icons-material/Star";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#100720",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(218,165,32,0.25)",
    gold: "#DAA520",
    goldGlow: "rgba(218,165,32,0.15)",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "rgba(0,0,0,0.5)",
    codeText: "#FFD700",
};

const CodeBox = ({ children }) => (
    <Box
        sx={{
            background: BRAND.codeBg,
            border: `1px solid ${BRAND.border}`,
            borderRadius: 2,
            p: 2,
            my: 2,
            fontFamily: "monospace",
            color: BRAND.codeText,
            whiteSpace: "pre-wrap",
            fontSize: 13,
        }}
    >
        {children}
    </Box>
);

export default function MySQLAssignmentSolution() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: 6 }}>
            <Container maxWidth="lg">

                {/* HERO */}
                <Chip
                    icon={<SchoolIcon sx={{ color: BRAND.gold }} />}
                    label="Capstone · Complete Solution"
                    sx={{ mb: 2, background: BRAND.goldGlow, color: BRAND.gold }}
                />

                <Typography variant="h3" sx={{ color: BRAND.white, fontWeight: 800, mb: 2 }}>
                    MySQL <span style={{ color: BRAND.gold }}>Assignment Solutions</span>
                </Typography>

                <Typography sx={{ color: BRAND.muted, maxWidth: 900 }}>
                    This page contains the **complete, professional solution** to the MySQL
                    Capstone Assignment. The solution reflects real‑world practices expected
                    from a **Database Administrator (DBA)** or **Backend Engineer**.
                </Typography>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* 1. DATABASE & SCHEMA */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700 }}>
                    1️⃣ Database & Table Design
                </Typography>

                <CodeBox>
                    {`CREATE DATABASE academy_pro;
USE academy_pro;

CREATE TABLE users (
  id INT PRIMARY KEY AUTO_INCREMENT,
  full_name VARCHAR(100) NOT NULL,
  email VARCHAR(120) UNIQUE NOT NULL,
  role ENUM('STUDENT','ADMIN') DEFAULT 'STUDENT',
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

CREATE TABLE courses (
  id INT PRIMARY KEY AUTO_INCREMENT,
  title VARCHAR(100) NOT NULL,
  price DECIMAL(8,2) NOT NULL
);

CREATE TABLE enrollments (
  user_id INT,
  course_id INT,
  enrolled_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  PRIMARY KEY (user_id, course_id),
  FOREIGN KEY (user_id) REFERENCES users(id),
  FOREIGN KEY (course_id) REFERENCES courses(id)
);

CREATE TABLE payments (
  id INT PRIMARY KEY AUTO_INCREMENT,
  user_id INT,
  amount DECIMAL(8,2),
  paid_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  FOREIGN KEY (user_id) REFERENCES users(id)
);`}
                </CodeBox>

                {/* 2. CRUD */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700, mt: 4 }}>
                    2️⃣ CRUD Operations
                </Typography>

                <CodeBox>
                    {`INSERT INTO users (full_name, email) VALUES
('Ada Lovelace', 'ada@academy.com'),
('Alan Turing', 'alan@academy.com');

INSERT INTO courses (title, price) VALUES
('SQL Fundamentals', 99.99),
('Advanced MySQL', 149.99);

SELECT * FROM users;
SELECT * FROM courses;

UPDATE users SET role='ADMIN' WHERE email='ada@academy.com';

DELETE FROM users WHERE email='ghost@academy.com';`}
                </CodeBox>

                {/* 3. JOINS */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700, mt: 4 }}>
                    3️⃣ Joins & Relationships
                </Typography>

                <CodeBox>
                    {`SELECT u.full_name, c.title
FROM users u
JOIN enrollments e ON e.user_id = u.id
JOIN courses c ON c.id = e.course_id;`}
                </CodeBox>

                {/* 4. FILTERING & FUNCTIONS */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700, mt: 4 }}>
                    4️⃣ Filtering, Sorting & Functions
                </Typography>

                <CodeBox>
                    {`SELECT COUNT(*) AS total_users FROM users;

SELECT AVG(price) FROM courses;

SELECT * FROM courses
WHERE price > 100
ORDER BY price DESC;`}
                </CodeBox>

                {/* 5. SUBQUERIES & VIEWS */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700, mt: 4 }}>
                    5️⃣ Subqueries & Views
                </Typography>

                <CodeBox>
                    {`SELECT *
FROM users
WHERE id IN (
  SELECT user_id FROM enrollments
);

CREATE VIEW student_payments AS
SELECT u.full_name, p.amount
FROM users u
JOIN payments p ON p.user_id = u.id;

SELECT * FROM student_payments;`}
                </CodeBox>

                {/* 6. INDEXING */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700, mt: 4 }}>
                    6️⃣ Indexes & Query Optimization
                </Typography>

                <CodeBox>
                    {`CREATE INDEX idx_users_email ON users(email);

EXPLAIN SELECT * FROM users WHERE email='ada@academy.com';`}
                </CodeBox>

                {/* 7. TRANSACTIONS */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700, mt: 4 }}>
                    7️⃣ Transactions & ACID
                </Typography>

                <CodeBox>
                    {`START TRANSACTION;

INSERT INTO payments (user_id, amount)
VALUES (1, 99.99);

COMMIT;`}
                </CodeBox>

                {/* 8. SECURITY */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700, mt: 4 }}>
                    8️⃣ Security & User Management
                </Typography>

                <CodeBox>
                    {`CREATE USER 'app_user'@'localhost' IDENTIFIED BY 'StrongPass!';

GRANT SELECT, INSERT, UPDATE
ON academy_pro.*
TO 'app_user'@'localhost';

REVOKE DELETE
ON academy_pro.*
FROM 'app_user'@'localhost';`}
                </CodeBox>

                {/* 9. STORED PROCEDURES & TRIGGERS */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700, mt: 4 }}>
                    9️⃣ Stored Procedures & Triggers
                </Typography>

                <CodeBox>
                    {`DELIMITER $$

CREATE PROCEDURE enroll_student(IN uid INT, IN cid INT)
BEGIN
  INSERT INTO enrollments (user_id, course_id)
  VALUES (uid, cid);
END$$

DELIMITER ;

CALL enroll_student(1,1);`}
                </CodeBox>

                {/* 10. APPLICATION INTEGRATION */}
                <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 700, mt: 4 }}>
                    🔟 MySQL with Applications
                </Typography>

                <CodeBox>
                    {`// Spring Boot (JPA)
@Entity
class User {
  @Id @GeneratedValue
  private Long id;
  private String fullName;
}

// Node.js
const [rows] = await pool.execute(
  'SELECT * FROM users WHERE id = ?',
  [id]
);`}
                </CodeBox>

                <Divider sx={{ my: 5, borderColor: BRAND.border }} />

                {/* FINAL NOTE */}
                <Box sx={{ p: 3, background: BRAND.goldGlow, borderRadius: 2 }}>
                    <Typography sx={{ color: BRAND.white }}>
                        <StarIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                        <strong>Result:</strong> Completing and understanding this solution
                        means you are ready for **real MySQL production work**, interviews,
                        and DBA‑level responsibilities.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
